// ============================================================
// About Page (About 2) — refreshed messaging for the SEO/GEO age
// ============================================================
import {
  FlaskConical,
  HeartHandshake,
  BarChart3,
  Lightbulb,
  Search,
  Sparkles,
  MousePointerClick,
  Repeat,
} from "lucide-react";

export const ABOUT_PAGE = {
  hero: {
    eyebrow: "About Slingshot SEO \u00b7 Est. 2006",
    titlePre: "Built on Search. ",
    titleAccent: "Rebuilt for the AI Age.",
    sub: "For nearly two decades we've engineered Page-1 dominance. Today we engineer visibility everywhere answers happen \u2014 Google, ChatGPT, AI Overviews, maps and paid media.",
    bg: "https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1920&auto=format&fit=crop",
  },
  belief: {
    heading:
      "Slingshot SEO exists because we love search \u2014 and we're endlessly fascinated by how it keeps changing the world.",
    body: "First it was ten blue links. Then maps, mobile and voice. Now AI engines answer before anyone clicks. Every shift rewrites the rules of visibility \u2014 and our job, since 2006, has been to know the new rules before anyone else.",
    image: "https://images.unsplash.com/photo-1531482615713-2afd69097998?q=80&w=1400&auto=format&fit=crop",
  },
  philosophy: [
    "Search done well has always been altruistic \u2014 a service that lives or dies on how well it serves people. That was true when results were ten blue links, and it's even more true now that an AI decides which three brands are worth mentioning at all.",
    "Generative engines reward exactly what honest search has always rewarded: genuine authority, real expertise, provable trust. Search remains the ultimate proving ground where the good guys win \u2014 the arena just got bigger. We work exclusively with deserving brands, and we help them win. Every time.",
  ],
  timeline: [
    {
      year: "2006",
      title: "Founded in Indianapolis",
      body: "A team of search scientists sets out to understand ranking better than anyone \u2014 and builds a practice on research, not folklore.",
    },
    {
      year: "2011-2013",
      title: "Three time Inc. 500 Honoree",
      body: "Named one of America's fastest-growing private companies \u2014 on the strength of client results, not promises.",
    },
    {
      year: "2018",
      title: "Acquired by Interact Marketing",
      body: "Interact Marketing, a division of Ecommerge, Inc takes the wheel.",
    },
    {
      year: "2025",
      title: "Search Changes Forever",
      body: "AI answers arrive above the results. We launch our GEO practice \u2014 engineering the entities, answers and authority that generative engines cite.",
    },
    {
      year: "Today",
      title: "The Web Marketing Machine\u2122",
      body: "Foundational SEO\u2122, GEO, advanced PPC and Retargeting delivered as one rapid-deployment system \u2014 with automation at the core and industry flavors built in.",
    },
  ],
  values: [
    {
      icon: FlaskConical,
      title: "Obsessed with the Science",
      body: "Search is physics, not folklore. We test, measure and prove \u2014 we never guess.",
    },
    {
      icon: HeartHandshake,
      title: "Good Guys Only",
      body: "We work exclusively with deserving brands \u2014 and help them win, every time.",
    },
    {
      icon: BarChart3,
      title: "Accountable to Revenue",
      body: "Rankings and citations are means. Pipeline and sales are the ends we answer for.",
    },
    {
      icon: Lightbulb,
      title: "Learn Loudly",
      body: "We share what we discover with the world. It keeps us sharp \u2014 and honest.",
    },
  ],
  mission:
    "To know more about search \u2014 human and machine \u2014 and to deliver greater business impact from it than anybody in the world.",
  vision:
    "To lead deserving brands to dominance across every place answers happen \u2014 search engines, AI engines, maps and paid media \u2014 and to share what we learn with the world.",
  mix: {
    heading: "One team. Every channel that matters.",
    sub: "The full-service mix behind every engagement \u2014 tuned to your market by The Web Marketing Machine\u2122.",
    items: [
      { icon: Search, label: "Foundational SEO\u2122", line: "The technical and authority bedrock." },
      { icon: Sparkles, label: "GEO", line: "Cited by ChatGPT, Perplexity & AI Overviews." },
      { icon: MousePointerClick, label: "Advanced PPC", line: "High-intent demand, captured today." },
      { icon: Repeat, label: "Retargeting", line: "The 97% who leave, brought back." },
    ],
  },
  culture: {
    image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=1400&auto=format&fit=crop",
  },
  cta: {
    heading: "See where you stand in the AI age.",
    sub: "Free Visibility Audit \u2014 your rankings, AI citations, paid efficiency and the fastest path to more leads. From a US-based strategist, in 2 business days or less.",
    primaryText: "Get My Free Visibility Audit",
    secondaryText: "Talk to a Strategist",
    secondaryHref: "https://www.slingshotseo.com/contact/",
  },
};

// ---------- About FAQ (also powers FAQPage schema) ----------
export const ABOUT_FAQ = {
  heading: "Questions we hear about Slingshot",
  items: [
    {
      q: "How long has Slingshot SEO been in business?",
      a: "Since 2006. We were founded in Indianapolis as a team of search scientists, became a three-time Inc. 500 honoree, and have spent nearly two decades staying ahead of every shift in search \u2014 including the AI one.",
    },
    {
      q: "What is GEO \u2014 and why does it matter?",
      a: "Generative Engine Optimization is how brands get cited by AI engines like ChatGPT, Perplexity and Google AI Overviews. As more answers happen without a click, GEO decides whether you're part of the answer or invisible.",
    },
    {
      q: "What makes Slingshot different from other agencies?",
      a: "Science over folklore. We test, measure and prove \u2014 and we're accountable to revenue, not vanity metrics. Every engagement is serviced by US-based strategists.",
    },
    {
      q: "What is The Web Marketing Machine\u2122?",
      a: "Our rapid-deployment delivery system: Foundational SEO\u2122, GEO, advanced PPC and retargeting engineered as one system, with automation at the core and industry flavors built in.",
    },
    {
      q: "Who do you work with?",
      a: "Deserving brands across Legal, Healthcare, Home Services, E-Commerce, Insurance Services and Franchise \u2014 companies with real expertise that buyers and AI engines can trust.",
    },
  ],
};
